/**
 * AccountSettings Component
 * The "Settings" tab of the account detail screen.
 *
 * Scope rule for this tab: it holds what is UNIQUE to configuration, plus the two
 * rare account actions. It deliberately does NOT restate the overview card that
 * sits above the tab bar on every tab.
 *
 * Removed (all pure duplication of that card, ~10px up the page):
 *   - Account Status / Session Status badges — the card's status pills, and the
 *     card also carries the live expiry countdown (SessionTimer) that this tab
 *     never had. Two renderings of one truth is one rendering too many.
 *   - Broker Platform — the card's <h2>.
 * Removed for a different reason:
 *   - Connect to Broker — a broker session must be re-established every market
 *     day, so it is the one FREQUENT action here and lives in the page header
 *     where it is reachable from every tab. The parent no longer passes
 *     onConnectAccount; do not re-add a Connect button to this tab.
 *
 * Edit + Delete are the inverse: rare, so they are NOT in the page header. This
 * tab is their only home.
 */

import React from "react";
import { Icon } from "../../../../components/ds";

// Matches the overview card's Created/Last Login rendering exactly. The card said
// "Jun 30, 2026" while this tab said "6/30/2026" — same field, same screen, two
// formats. Locale-independent by construction: en-US is pinned, not inherited.
const formatDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// label/value row. `hint` explains a field whose name is not self-evident.
// Values that are absent render as muted placeholder text rather than being
// dropped — an empty setting is information.
const SettingRow = ({ label, hint, value, placeholder = "Not set" }) => (
  <div className="flex items-start justify-between gap-4 px-4 py-3 border-b border-[var(--divider)] last:border-b-0">
    <div className="min-w-0">
      <div className="text-sm text-fg-2">{label}</div>
      {hint ? <div className="text-xs text-fg-3 mt-0.5">{hint}</div> : null}
    </div>
    <div className="min-w-0 text-sm text-right break-words">
      {value ? (
        <span className="font-medium text-fg-1">{value}</span>
      ) : (
        <span className="text-fg-3">{placeholder}</span>
      )}
    </div>
  </div>
);

const SectionTitle = ({ icon, title, caption }) => (
  <div className="flex items-center gap-3 mb-3">
    <Icon name={icon} className="text-accent shrink-0" size={20} />
    <div className="min-w-0">
      <h3 className="text-sm font-semibold text-fg-1">{title}</h3>
      {caption ? <p className="text-xs text-fg-2 mt-0.5">{caption}</p> : null}
    </div>
  </div>
);

// Prop names MUST match what AccountDetails actually passes (onEditAccount /
// onDeleteAccount). This signature previously asked for `onOpenLoginModal`,
// which the parent has never passed, and omitted `onEditAccount` entirely — so
// two of the three buttons were wired to undefined and silently did nothing on
// every device.
const AccountSettings = ({ account, onEditAccount, onDeleteAccount }) => {
  // `account.account_name` is not a field on broker_accounts and never has been
  // (the column is app_name — see backend/broker-integration/app/models/broker.py).
  // The old row read that ghost key, so it rendered "Not specified" for every
  // account on every screen regardless of what the user had actually entered.
  const appName = account.app_name;
  const createdAt = formatDate(account.created_at);
  const updatedAt = formatDate(account.updated_at);

  return (
    <div className="space-y-4">
      {/* No "Account Settings" heading: the tab is already labelled "Settings".
          The section titles below carry the meaning the tab label does not. */}
      <div className="bg-bg-2 rounded-lg shadow-sm border border-[var(--divider)] p-4 sm:p-6">
        <SectionTitle
          icon="settings"
          title="Configuration"
          caption="How this account is registered with the broker"
        />
        <div className="rounded-lg border border-[var(--divider)] bg-bg-3/50 overflow-hidden">
          <SettingRow
            label="Account Name"
            hint="App name registered with the broker"
            value={appName}
          />
        </div>
      </div>

      <div className="bg-bg-2 rounded-lg shadow-sm border border-[var(--divider)] p-4 sm:p-6">
        <SectionTitle
          icon="calendar"
          title="Record"
          caption="When this account was added and last changed"
        />
        <div className="rounded-lg border border-[var(--divider)] bg-bg-3/50 overflow-hidden">
          <SettingRow label="Created" value={createdAt} placeholder="Unknown" />
          <SettingRow
            label="Last Updated"
            hint="Last change to these settings"
            value={updatedAt}
            placeholder="Never"
          />
        </div>
      </div>

      <div className="bg-bg-2 rounded-lg shadow-sm border border-[var(--divider)] p-4 sm:p-6">
        <SectionTitle
          icon="user"
          title="Account Actions"
          caption="Infrequent changes to the account itself"
        />

        <button
          type="button"
          className="btn btn-secondary w-full sm:w-auto justify-start"
          onClick={onEditAccount}
        >
          <Icon name="edit" size={16} />
          Edit Account Details
        </button>

        {/* Delete is fenced off rather than sitting 8px under Edit: it was the
            last item in a tight stack, so a mis-scroll tap landed on it. The
            window.confirm in the parent is the real gate — this is spacing and a
            stated consequence, which is what makes the confirm readable rather
            than reflexive. */}
        <div className="mt-5 pt-5 border-t border-[var(--divider)]">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="min-w-0">
              <div className="text-sm font-medium text-fg-1">Delete Account</div>
              <p className="text-xs text-fg-2 mt-0.5">
                Removes this account and its stored credentials. This cannot be undone.
              </p>
            </div>
            <button
              type="button"
              className="btn btn-danger w-full sm:w-auto justify-center shrink-0"
              onClick={onDeleteAccount}
            >
              <Icon name="delete" size={16} />
              Delete Account
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountSettings;
